import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Menu, Bot, Plug } from 'lucide-react';
import { useChatLayout } from '@/components/layout/ChatLayout';
import { mcpConnectionsService, type AiApp, type McpConnection } from '@/services/mcpConnectionsService';
import { handleApiError } from '@/services/api';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

const STATUS_STYLES: Record<McpConnection['status'], string> = {
  approved: 'bg-success/15 text-success',
  revoked: 'bg-muted text-muted-foreground',
};

const APP_LABELS: Record<AiApp, string> = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  claude_code: 'Claude Code',
  other: 'External AI',
};

function displayName(c: McpConnection): string {
  if (c.app !== 'other') return APP_LABELS[c.app];
  return c.client_name || c.client_id;
}

function usageLine(c: McpConnection): string {
  if (c.status === 'revoked') return 'Reconnect from the AI app to restore access';
  const parts: string[] = [];
  if (c.approved_at) parts.push(`connected ${new Date(c.approved_at).toLocaleDateString()}`);
  parts.push(c.last_used_at ? `last used ${new Date(c.last_used_at).toLocaleDateString()}` : 'not used yet');
  return parts.join(' · ');
}

export default function ExternalAiAccessPage() {
  const { isMobile, toggleSidebar } = useChatLayout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const connectionsQuery = useQuery({
    queryKey: ['mcpConnections'],
    queryFn: mcpConnectionsService.list,
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => mcpConnectionsService.revoke(id),
    onSuccess: () => {
      toast.success('Access revoked');
      queryClient.invalidateQueries({ queryKey: ['mcpConnections'] });
    },
    onError: (e) => toast.error(handleApiError(e) || 'Failed to revoke'),
  });

  if (connectionsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const connections = connectionsQuery.data?.connections ?? [];

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-4xl mx-auto space-y-6">
        <Link
          to="/settings"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </Link>

        <div>
          <h1 className="text-3xl font-bold text-foreground">External AI Access</h1>
          <p className="text-muted-foreground mt-1">
            AI assistants (Claude.ai, ChatGPT, …) connected to your Zamino data. Each app connects
            through its own sign-in + consent, and can be revoked here independently.
          </p>
        </div>

        <Button size="sm" variant="outline" onClick={() => navigate('/connect-ai')}>
          <Plug className="w-4 h-4 mr-1.5" /> Connect a new AI app
        </Button>

        <div className="rounded-xl bg-muted border border-border divide-y divide-border [&>*:first-child]:rounded-t-xl [&>*:last-child]:rounded-b-xl">
          {connections.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No external AI connections yet. Connect Zamino from an AI assistant and approve it on
              the consent screen — it will appear here.
            </div>
          ) : (
            connections.map((c) => {
              const revoking = revokeMutation.isPending && revokeMutation.variables === c.id;
              return (
                <div key={c.id} className="flex items-center justify-between gap-3 p-4 sm:p-5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-card border border-border flex items-center justify-center flex-shrink-0">
                      <Bot className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{displayName(c)}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {usageLine(c)}
                        <span
                          className={`ml-2 inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium capitalize ${STATUS_STYLES[c.status]}`}
                        >
                          {c.status === 'approved' ? 'connected' : 'revoked'}
                        </span>
                      </p>
                    </div>
                  </div>

                  {c.status === 'approved' && (
                    <Button
                      size="sm"
                      variant="destructive"
                      className="flex-shrink-0"
                      isLoading={revoking}
                      onClick={() => revokeMutation.mutate(c.id)}
                    >
                      Revoke
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
